import { Facebook, Instagram, Youtube, Phone, Mail, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import logo from "@/assets/logo.webp";
import { useSiteLogo } from "@/hooks/useSiteLogo";
const footerTranslations = {
  sv: {
    slogan: "Gör världen till en rundare plats",
    contact: "Kontakt",
    information: "Information",
    followUs: "Följ oss",
    useCases: "Användningsområden",
    tradgardskupol: "Trädgårdskupol",
    vaxthus: "Växthus & orangeri",
    glamping: "Glamping",
    termsOfPurchase: "Köpvillkor",
    rightOfWithdrawal: "Ångerrätt",
    privacyPolicy: "Integritetspolicy",
    cookiePolicy: "Cookiepolicy",
    shippingPolicy: "Leveranspolicy",
    becomePartner: "Bli partner med oss",
    copyright: "Alla rättigheter förbehållna."
  },
  en: {
    slogan: "Making the world a rounder place",
    contact: "Contact",
    information: "Information",
    followUs: "Follow us",
    useCases: "Use cases",
    tradgardskupol: "Garden dome",
    vaxthus: "Greenhouse & orangery",
    glamping: "Glamping",
    termsOfPurchase: "Terms of Purchase",
    rightOfWithdrawal: "Right of Withdrawal",
    privacyPolicy: "Privacy Policy",
    cookiePolicy: "Cookie Policy",
    shippingPolicy: "Shipping Policy",
    becomePartner: "Become a Partner",
    copyright: "All rights reserved."
  },
  de: {
    slogan: "Die Welt runder machen",
    contact: "Kontakt",
    information: "Information",
    followUs: "Folgen Sie uns",
    useCases: "Anwendungsbereiche",
    tradgardskupol: "Gartenkuppel",
    vaxthus: "Gewächshaus & Orangerie",
    glamping: "Glamping",
    termsOfPurchase: "Kaufbedingungen",
    rightOfWithdrawal: "Widerrufsrecht",
    privacyPolicy: "Datenschutzrichtlinie",
    cookiePolicy: "Cookie-Richtlinie",
    shippingPolicy: "Lieferrichtlinie",
    becomePartner: "Partner werden",
    copyright: "Alle Rechte vorbehalten."
  }
};
export const Footer = () => {
  const {
    language
  } = useLanguage();
  const t = footerTranslations[language];
  const footerLogo = useSiteLogo(logo);
  return <footer id="contact" className="bg-primary text-primary-foreground pt-12 pb-0 px-6">
      <div className="container mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-8">
          {/* Company Info */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <img 
                alt="Diamond Domes" 
                className="h-20 w-auto" 
                src={footerLogo}
                width="160"
                height="80"
                loading="lazy"
                decoding="async"
              />
            </div>
            <p className="text-primary-foreground/80 text-sm">
              {t.slogan}
            </p>
          </div>
          
          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-serif font-semibold mb-3">{t.contact}</h3>
            <div className="space-y-2 text-sm">
              <a href="tel:+46793483581" className="flex items-center space-x-2 hover:text-primary-foreground/80 transition-smooth">
                <Phone className="w-4 h-4" />
                <span>+46 79 348 35 81</span>
              </a>
              <a href="mailto:info@diamonddomes.se" className="flex items-center space-x-2 hover:text-primary-foreground/80 transition-smooth">
                <Mail className="w-4 h-4" />
                <span>info@diamonddomes.se</span>
              </a>
              <div className="flex items-start space-x-2">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Stationsgatan 11d<br />655 60 Molkom</span>
              </div>
            </div>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="text-lg font-serif font-semibold mb-3">{t.information}</h3>
            <div className="space-y-1.5 text-sm">
              <Link to="/kopvillkor" className="block hover:text-primary-foreground/80 transition-smooth">
                {t.termsOfPurchase}
              </Link>
              <Link to="/angerratt" className="block hover:text-primary-foreground/80 transition-smooth">
                {t.rightOfWithdrawal}
              </Link>
              <Link to="/integritetspolicy" className="block hover:text-primary-foreground/80 transition-smooth">
                {t.privacyPolicy}
              </Link>
              <Link to="/cookiepolicy" className="block hover:text-primary-foreground/80 transition-smooth">
                {t.cookiePolicy}
              </Link>
              <Link to="/leveranspolicy" className="block hover:text-primary-foreground/80 transition-smooth">
                {t.shippingPolicy}
              </Link>
            </div>
          </div>

          {/* Use Cases */}
          <div>
            <h3 className="text-lg font-serif font-semibold mb-3">
              <Link to="/anvandningsomraden" className="hover:text-primary-foreground/80 transition-smooth">
                {t.useCases}
              </Link>
            </h3>
            <div className="space-y-1.5 text-sm">
              <Link to="/anvandningsomraden/tradgardskupol" className="block hover:text-primary-foreground/80 transition-smooth">
                {t.tradgardskupol}
              </Link>
              <Link to="/anvandningsomraden/kupol-vaxthus" className="block hover:text-primary-foreground/80 transition-smooth">
                {t.vaxthus}
              </Link>
              <Link to="/anvandningsomraden/glamping-kupol" className="block hover:text-primary-foreground/80 transition-smooth">
                {t.glamping}
              </Link>
            </div>
          </div>

          {/* Social Media */}
          <div>
            <h3 className="text-lg font-serif font-semibold mb-3">{t.followUs}</h3>
            <div className="flex space-x-3">
              <a href="https://www.facebook.com/profile.php?id=61581115333244" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-9 h-9 bg-primary-foreground/10 rounded-full flex items-center justify-center hover:bg-primary-foreground/20 transition-smooth">
                <Facebook className="w-4 h-4" />
              </a>
              <a href="https://www.instagram.com/dd36200/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-9 h-9 bg-primary-foreground/10 rounded-full flex items-center justify-center hover:bg-primary-foreground/20 transition-smooth">
                <Instagram className="w-4 h-4" />
              </a>
              <a href="https://www.youtube.com/@dd362-0" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="w-9 h-9 bg-primary-foreground/10 rounded-full flex items-center justify-center hover:bg-primary-foreground/20 transition-smooth">
                <Youtube className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
        
        {/* Copyright above the line */}
        <div className="flex items-center justify-center text-xs text-primary-foreground/60 pb-4 relative">
          <Link 
            to="/bli-partner" 
            className="hover:text-primary-foreground/80 transition-smooth mr-2"
          >
            {t.becomePartner}
          </Link>
          <span className="mr-2">|</span>
          <p>© {new Date().getFullYear()} Diamond Domes. {t.copyright}</p>
          <Link 
            to="/partners/login" 
            className="absolute right-0 opacity-0 hover:opacity-60 transition-opacity duration-300 text-[10px] text-primary-foreground/40"
          >
            för partners
          </Link>
        </div>
        
        {/* Bottom border line */}
        <div className="border-t border-primary-foreground/20"></div>
      </div>
    </footer>;
};